import { accessToken, AUTH_SESSION_KEY, AUTH_SESSION_SSO_KEY, ensureSession, readSession,
  requireSignIn, SESSION_CHANGED, tokenValid } from './session';
import axios from "axios";
import config from "../../config";
export { AUTH_SESSION_SSO_KEY } from './session';

const sessionRequestError = (error: any) => error?.response ? error : Object.assign(
  new Error(error?.message || 'Could not renew your session. Please try saving again.'),
  { response: { status: 0, data: { message: error?.message || 'Could not renew your session. Please try saving again.' } } }
);
const sessionAccount = (session: any) => JSON.stringify([
  session?.user?.id ?? session?.id ?? null, session?.user?.email ?? session?.email ?? null,
]);

// content type
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.baseURL = config.API_URL;
axios.interceptors.request.use(
  async (request: any) => {
    let user = getUserFromSession();
    const pathsToCheck = [
      "/quote",
      "/auth",
      "/clients",
      "/contacts",
      "/addresses",
      "/files",
      "/tech-service-orders",
      "/consumables",
      "/tech-orders",
      "/fullfillment",
      "/deals",
      "/roles",
    ];
    const isMatchingPath = pathsToCheck.some((path) =>
      request.url.includes(path)
    );
    if (request.url.includes("sso")) {
      request.baseURL = config.API_URL_V2;
      request.headers["Authorization"] =
        "Bearer " + sessionStorage.getItem(AUTH_SESSION_SSO_KEY);
    } else if (request.url.includes("/v1/companies") || request.url.includes("/v1/devices")) {
      request.baseURL = config.PRINTHUB_API_URL;
      if (user && user.tokens && user.tokens.access) {
        request.headers["Authorization"] =
          "Bearer " + user?.tokens?.access?.token;
      }
    } else if (isMatchingPath) {
      request.baseURL = config.API_URL_V2;
      if (user && user.tokens && user.tokens.access) {
        request.headers["Authorization"] =
          "Bearer " + user?.tokens?.access?.token;
      }
    } else {
      request.baseURL = config.API_URL;
      if (user && user.token) {
        request.headers["Authorization"] = "Bearer " + user.token;
      }
    }

    const bootstrap = /\/auth\/(login|refresh-tokens|sso)(?:[/?]|$)/.test(request.url);
    // Axios appends relative URLs to the base path, even when they start with '/'.
    const absolute = /^([a-z][a-z\d+.-]*:)?\/\//i.test(request.url);
    const target = new URL(absolute ? request.url
      : `${(request.baseURL || window.location.origin).replace(/\/$/, '')}/${request.url.replace(/^\/+/, '')}`, window.location.origin);
    const trusted = [config.API_URL, config.API_URL_V2, config.PRINTHUB_API_URL].some(base => {
      if (!base) return false;
      const api = new URL(base, window.location.origin);
      return target.origin === api.origin && target.pathname.startsWith(api.pathname.replace(/\/$/, '') + '/');
    });
    if (user && !bootstrap && trusted) {
      request._sessionManaged = true;
      try { user = await ensureSession(); }
      catch (error) { throw sessionRequestError(error); }
      request._sessionAccount = sessionAccount(user);
      request.headers.Authorization = 'Bearer ' + (isMatchingPath || request.url.includes('/v1/companies')
        || request.url.includes('/v1/devices') ? accessToken(user) : user.token || accessToken(user));
    }
    return request;
  },
  (error) => Promise.reject(error)
);
axios.interceptors.response.use(response => response, async error => {
  const request = error.config;
  if (error.response?.status !== 401 || !request?._sessionManaged) return Promise.reject(error);
  if (request._sessionRetried) { requireSignIn(); return Promise.reject(error); }
  request._sessionRetried = true;
  try {
    const current = readSession();
    if (!current || request._sessionAccount !== sessionAccount(current)) return Promise.reject(error);
    const sentToken = request.headers?.Authorization?.replace(/^Bearer /, '');
    // A concurrent request may already have renewed this token.
    if (!current || sentToken === accessToken(current) || !tokenValid(accessToken(current))) await ensureSession(true);
    return axios.request(request);
  } catch (refreshError) { return Promise.reject(sessionRequestError(refreshError)); }
});

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string | null) => {
  if (token) axios.defaults.headers.common["Authorization"] = "Bearer " + token;
  else delete axios.defaults.headers.common["Authorization"];
};

const getUserFromSession = () => {
  return readSession();
};
class APICore {
  /**
   * Fetches data from given url
   */
  get = (url: string, params: any) => {
    let response;
    if (params) {
      var queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }
    return response;
  };

  getFile = (url: string, params: any) => {
    let response;
    if (params) {
      var queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
      response = axios.get(`${url}?${queryString}`, { responseType: "blob" });
    } else {
      response = axios.get(`${url}`, { responseType: "blob" });
    }
    return response;
  };

  getMultiple = (urls: string, params: any) => {
    const reqs = [];
    let queryString = "";
    if (params) {
      queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
    }

    for (const url of urls) {
      reqs.push(axios.get(`${url}?${queryString}`));
    }
    return axios.all(reqs);
  };

  /**
   * post given data to url
   */
  create = (url: string, data: any) => {
    return axios.post(url, data);
  };

  /**
   * Updates patch data
   */
  updatePatch = (url: string, data: any) => {
    return axios.patch(url, data);
  };

  /**
   * Updates data
   */
  update = (url: string, data: any) => {
    return axios.put(url, data);
  };

  /**
   * Deletes data
   */
  delete = (url: string, params?: any) => {
    let response;
    if (params) {
      var queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
      response = axios.delete(`${url}?${queryString}`, params);
    } else {
      response = axios.delete(`${url}`);
    }
    return response;
  };

  /**
   * post given data to url with file
   */
  createWithFile = (url: string, data: any) => {
    const formData = new FormData();
    for (const k in data) {
      formData.append(k, data[k]);
    }

    const config: any = {
      headers: {
        ...axios.defaults.headers,
        "content-type": "multipart/form-data",
      },
    };
    return axios.post(url, formData, config);
  };

  /**
   * post given data to url with file
   */
  updateWithFile = (url: string, data: any) => {
    const formData = new FormData();
    for (const k in data) {
      formData.append(k, data[k]);
    }

    const config: any = {
      headers: {
        ...axios.defaults.headers,
        "content-type": "multipart/form-data",
      },
    };
    return axios.patch(url, formData, config);
  };

  isUserAuthenticated = () => {
    const user = this.getLoggedInUser();

    return !!user && tokenValid(accessToken(user));
  };

  setLoggedInUser = (session: any) => {
    if (session) {
      const updatedSession = {
        ...session,
      };
      sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(updatedSession));
    } else {
      sessionStorage.removeItem(AUTH_SESSION_KEY);
      sessionStorage.removeItem(AUTH_SESSION_SSO_KEY);
    }
    window.dispatchEvent(new Event(SESSION_CHANGED));
  };
  setLoggedInSSOUser = (session: any) => {
    if (session) {
      const updatedSession = {
        ...session,
        authMethod: 'sso',
        tokens: {
          access: { token: sessionStorage.getItem(AUTH_SESSION_SSO_KEY) },
        },
      };
      sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(updatedSession));
    } else {
      sessionStorage.removeItem(AUTH_SESSION_KEY);
      sessionStorage.removeItem(AUTH_SESSION_SSO_KEY);
    }
    window.dispatchEvent(new Event(SESSION_CHANGED));
  };
  /**
   * Returns the logged in user
   */
  getLoggedInUser = () => {
    return getUserFromSession();
  };

  setUserInSession = (modifiedUser: any) => {
    let userInfo = sessionStorage.getItem(AUTH_SESSION_KEY);
    if (userInfo) {
      const { token, tokens, user } = JSON.parse(userInfo);
      this.setLoggedInUser({ token, tokens, ...user, ...modifiedUser });
    }
  };
}

/*
Check if token available in session
*/
let user = getUserFromSession();
if (user) {
  const { token } = user;
  if (token) {
    setAuthorization(token);
  }
}

export { APICore, setAuthorization };
